import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { useUserInfo } from '../../contexts/UserContext';
import { useFlashMessage } from '../FlashMessage';
import { dashboardService } from '../../services/dashboardService';
import HAMSTIRUploadModal from './HAMSTIRUploadModal';
import HAMSTIRResultsModal from './HAMSTIRResultsModal';
import ConfirmationPopup from '../ConfirmationPopup';
import { getErrorMessage } from '../../utils/errorMessages';
import { confirmName, lookupNames } from '../../services/documentNamingService';
import SuggestedName from '../DocumentNaming/SuggestedName';
import useSuggestionPolling from '../DocumentNaming/useSuggestionPolling';
import { DocumentReadingRunPanel, runOutcome, useDocumentReadingRun } from './documentReadingRun';
import ReadingProgress from '../ui/ReadingProgress';
import '../CRMProfileScreen.css';

const HAMSTIRBuilderBlock = ({ profileData, onDataChange, isNewProfile, onProfileUpdate }) => {
    const { userInfo } = useUserInfo();
    const { showError, showSuccess, showInfo } = useFlashMessage();
    
    // Document management state following AI Notes pattern
    const [hamstirDocuments, setHamstirDocuments] = useState([]);
    const [hamstirDisplayNames, setHamstirDisplayNames] = useState({});
    useSuggestionPolling(hamstirDisplayNames, setHamstirDisplayNames, 'partner_info');
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [documentError, setDocumentError] = useState(null);
    const [showDocuments, setShowDocuments] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    
    // Delete confirmation state
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    
    // Duplicate detection state
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateFileInfo, setDuplicateFileInfo] = useState(null);
    
    // Current user info for employee attribution
    const [currentUserInfo, setCurrentUserInfo] = useState(null);
    
    // Employee information state for document metadata
    const [employeeInfoCache, setEmployeeInfoCache] = useState({});

    // HAMSTIR Builder state
    const [partnerIdentifier, setPartnerIdentifier] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [submittingBuild, setSubmittingBuild] = useState(false);
    const [buildResults, setBuildResults] = useState(null);
    const [buildSourceUrls, setBuildSourceUrls] = useState([]);
    const [showResultsModal, setShowResultsModal] = useState(false);

    // The summary is built in the background (reading a scanned document takes
    // minutes). It is only called a success when every chosen document was read;
    // otherwise the block names the ones that were not, and why.
    const handleBuildFinished = (finishedRun) => {
        if (finishedRun.status === 'done') {
            setBuildResults(finishedRun.result?.partner_overview_summary || '');
            setBuildSourceUrls(finishedRun.result?.source_urls || []);
            setShowResultsModal(true);
            setPartnerIdentifier(profileData?.legal_entity_name || profileData?.trading_short_name || profileData?.company_website_url || '');
            setSelectedFiles([]);
        }
        const outcome = runOutcome(finishedRun, 'Partner profile summary generated successfully!');
        if (outcome.tone === 'success') showSuccess(outcome.text);
        else if (outcome.tone === 'warning') showInfo(outcome.text, 10000);
        else showError(outcome.text, 10000);
    };
    const {
        run: buildRun,
        active: buildRunActive,
        start: startBuildRun,
        clear: clearBuildRun,
    } = useDocumentReadingRun(handleBuildFinished, { kind: 'partner_profile', partnerId: profileData?.id });
    const building = submittingBuild || buildRunActive;

    // HAMSTIR Document Types (SUBJECT field categories)
    const HAMSTIR_DOCUMENT_TYPES = [
        'Company Profile',
        'Financial Reports',
        'Trade Documentation',
        'Legal Documents',
        'Compliance Records',
        'Market Research',
        'Contact Information',
        'Other'
    ];

    useEffect(() => {
        if (profileData) {
            // Set partner identifier from existing profile data
            const identifier = profileData.legal_entity_name || profileData.trading_short_name || profileData.company_website_url || '';
            setPartnerIdentifier(identifier);
        }
    }, [profileData]);

    // Get current user information for employee_id
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userInfoData = await dashboardService.getUserInfo();
                setCurrentUserInfo(userInfoData);
            } catch (error) {
                console.error('Error fetching user info:', error);
                setCurrentUserInfo(userInfo);
            }
        };

        if (!currentUserInfo && userInfo?.user_id) {
            setCurrentUserInfo(userInfo);
        } else if (!currentUserInfo) {
            fetchUserInfo();
        }
    }, [userInfo, currentUserInfo]);

    // Auto-fetch HAMSTIR documents when component mounts and profile is available
    useEffect(() => {
        if (profileData?.id) {
            console.log('🏗️ FRONTEND: HAMSTIR Builder mounted for partner:', profileData.id);
            console.log('🏗️ FRONTEND: Partner name:', profileData.legal_entity_name);
            console.log('🏗️ FRONTEND: Auto-fetching documents...');
            fetchDocuments();
            setShowDocuments(true);
        } else {
            console.log('⚠️ FRONTEND: HAMSTIR Builder mounted but no profile data available');
        }
    }, [profileData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Document management functions following AI Notes pattern
    const fetchDocuments = async () => {
        if (!profileData?.id) return;
        
        setLoadingDocuments(true);
        setDocumentError(null);
        
        try {
            console.log('Fetching HAMSTIR documents for partner ID:', profileData.id);
            
            const response = await axiosInstance.get('/api/docuware/general-partner-info-documents/documents/', {
                params: {
                    partner_id: profileData.id,
                    count: 100
                }
            });
            
            const documents = response.data.Items || [];
            setHamstirDocuments(documents);
            
            console.log('Successfully fetched', documents.length, 'HAMSTIR documents');
            
            try {
                const docIds = documents.map(d => String(d?.Id)).filter(Boolean);
                if (docIds.length > 0) {
                    const map = await lookupNames(docIds, 'partner_info');
                    setHamstirDisplayNames(map || {});
                }
            } catch (lookupErr) {
                console.warn('Failed to lookup AI display names for HAMSTIR (non-fatal):', lookupErr);
            }
            
        } catch (error) {
            console.error('Error fetching HAMSTIR documents:', error);
            setDocumentError(error.response?.data?.error || 'Failed to load HAMSTIR documents');
        } finally {
            setLoadingDocuments(false);
        }
    };

    // No grouping needed - display all documents in a simple list

    // Duplicate detection helper functions
    const checkForDuplicateFilename = (filename) => {
        console.log('🔍 FRONTEND: Checking for duplicate filename:', filename);
        console.log('🔍 FRONTEND: Current documents:', hamstirDocuments);
        
        if (!hamstirDocuments || hamstirDocuments.length === 0) {
            console.log('✅ FRONTEND: No existing documents, no duplicates found');
            return { isDuplicate: false, existingDocument: null };
        }
        
        // Check if any existing document has the same filename in SUBJECT field
        const duplicateDocument = hamstirDocuments.find(doc => {
            const docInfo = getDocumentInfo(doc);
            const isDup = docInfo.subject === filename;
            console.log(`🔍 FRONTEND: Comparing "${docInfo.subject}" with "${filename}": ${isDup}`);
            return isDup;
        });
        
        if (duplicateDocument) {
            console.log('⚠️ FRONTEND: Duplicate found:', duplicateDocument);
            return { isDuplicate: true, existingDocument: duplicateDocument };
        }
        
        console.log('✅ FRONTEND: No duplicates found');
        return { isDuplicate: false, existingDocument: null };
    };

    const generateIncrementedFilename = (originalFilename) => {
        console.log('🔢 FRONTEND: Generating incremented filename for:', originalFilename);
        
        if (!hamstirDocuments || hamstirDocuments.length === 0) {
            console.log('✅ FRONTEND: No existing documents, returning original filename');
            return originalFilename;
        }
        
        // Extract existing filenames from SUBJECT fields
        const existingFilenames = hamstirDocuments.map(doc => {
            const docInfo = getDocumentInfo(doc);
            return docInfo.subject;
        }).filter(Boolean); // Remove any null/undefined values
        
        console.log('📋 FRONTEND: Existing filenames:', existingFilenames);
        
        // Parse the original filename to extract name and extension
        const lastDotIndex = originalFilename.lastIndexOf('.');
        const nameWithoutExt = lastDotIndex !== -1 ? originalFilename.substring(0, lastDotIndex) : originalFilename;
        const extension = lastDotIndex !== -1 ? originalFilename.substring(lastDotIndex) : '';
        
        console.log('📝 FRONTEND: Name without extension:', nameWithoutExt);
        console.log('📝 FRONTEND: Extension:', extension);
        
        // Start checking from (1) and increment until we find a unique name
        let counter = 1;
        let newFilename = `${nameWithoutExt} (${counter})${extension}`;
        
        while (existingFilenames.includes(newFilename)) {
            counter++;
            newFilename = `${nameWithoutExt} (${counter})${extension}`;
            console.log(`🔢 FRONTEND: Trying counter ${counter}: ${newFilename}`);
        }
        
        console.log('✅ FRONTEND: Generated unique filename:', newFilename);
        return newFilename;
    };

    // Handle HAMSTIR file upload following AI Notes pattern
    const handleFileUpload = async (documentType, file, namingPayload) => {
        // Validation
        if (!profileData?.id) {
            showError('Please save the partner profile first before uploading documents.');
            return;
        }
        
        if (!documentType) {
            showError('Please select a document type.');
            return;
        }
        
        // File size validation (30MB)
        const maxSizeInBytes = 30 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            showError(`File size exceeds 30MB limit.`);
            return;
        }
        
        if (namingPayload) {
            try { file._namingPayload = namingPayload; } catch (e) {}
        }
        
        console.log('📤 FRONTEND: Starting HAMSTIR file upload process');
        console.log('📄 FRONTEND: File details:', { name: file.name, size: file.size, type: file.type });
        
        // Check for duplicate filename before proceeding with upload
        const duplicateCheck = checkForDuplicateFilename(file.name);
        
        if (duplicateCheck.isDuplicate) {
            console.log('⚠️ FRONTEND: Duplicate detected, showing modal');
            // Store the upload details for later processing
            setDuplicateFileInfo({
                documentType,
                file,
                existingDocument: duplicateCheck.existingDocument
            });
            setShowDuplicateModal(true);
            return; // Stop here and wait for user decision
        }
        
        // No duplicate found, proceed with normal upload
        console.log('✅ FRONTEND: No duplicate found, proceeding with upload');
        await performFileUpload(documentType, file, file.name);
    };

    // Separate function to handle the actual upload process
    const performFileUpload = async (documentType, file, finalFilename) => {
        console.log('🚀 FRONTEND: Performing file upload with filename:', finalFilename);
        
        setUploading(true);
        
        try {
            const employeeId = currentUserInfo?.user_id || '1';
            const partnerName = profileData?.legal_entity_name || 'Unknown Partner';
            const subject = finalFilename; // Use the final filename (potentially renamed) as subject
            
            console.log('📋 FRONTEND: Upload metadata:', {
                employeeId,
                partnerName,
                subject,
                documentType
            });
            
            // Step 1: Create document record
            const recordData = {
                partner_name: partnerName,
                employee_id: employeeId,
                partner_id: profileData?.id.toString(),
                subject: subject
            };
            
            console.log('📝 FRONTEND: Creating document record...');
            const recordResponse = await axiosInstance.post('/api/docuware/general-partner-info-documents/create-record/', recordData);
            
            if (!recordResponse.data.success || !recordResponse.data.document_id) {
                throw new Error('Failed to create document record');
            }
            
            const documentId = recordResponse.data.document_id;
            console.log('✅ FRONTEND: Document record created with ID:', documentId);
            
            // Step 2: Upload file
            console.log('📤 FRONTEND: Uploading file content...');
            const fileFormData = new FormData();
            fileFormData.append('file', file);
            
            await axiosInstance.post(`/api/docuware/general-partner-info-documents/upload-file/${documentId}/`, fileFormData);
            
            console.log('🎉 FRONTEND: File upload completed successfully');
            
            try {
                const namingPayload = file._namingPayload;
                if (namingPayload && namingPayload.components) {
                    await confirmName({
                        docuwareDocumentId: String(documentId),
                        module: 'partner_info',
                        file,
                        typeDropdownValue: namingPayload.typeDropdownValue || '',
                        partnerName: namingPayload.partnerName || '',
                        kycSubtype: namingPayload.kycSubtype || '',
                        relatedEntityId: profileData?.id ? String(profileData.id) : '',
                        components: namingPayload.components,
                        aiSuggestion: namingPayload.aiSuggestion || {},
                        userOverrides: namingPayload.userOverrides || [],
                        extractionConfidence: namingPayload.extractionConfidence ?? null,
                        originalFilename: finalFilename,
                    });
                    console.log('AI display name persisted for HAMSTIR doc:', documentId);
                }
            } catch (nameErr) {
                console.warn('Failed to persist AI display name (non-fatal):', nameErr);
            }
            
            showSuccess(`HAMSTIR document "${finalFilename}" uploaded successfully for ${documentType}!`);
            
            // Refresh document list
            if (showDocuments) {
                console.log('🔄 FRONTEND: Refreshing document list...');
                setTimeout(() => {
                    fetchDocuments();
                }, 1000);
            }
            
        } catch (error) {
            console.error('❌ FRONTEND: HAMSTIR upload error:', error);
            showError(getErrorMessage(error, 'Profile generation failed. Please try again.'));
        } finally {
            setUploading(false);
        }
    };

    // Duplicate modal handlers
    const handleDuplicateKeepBoth = async () => {
        console.log('👥 FRONTEND: User chose to keep both files');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate file info available');
            return;
        }
        
        const { documentType, file } = duplicateFileInfo;
        const incrementedFilename = generateIncrementedFilename(file.name);
        
        console.log('📝 FRONTEND: Renaming file to:', incrementedFilename);
        
        // Close modal and proceed with upload using incremented filename
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
        
        await performFileUpload(documentType, file, incrementedFilename);
    };

    const handleDuplicateReplace = async () => {
        console.log('🔄 FRONTEND: User chose to replace existing file');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate file info available');
            return;
        }
        
        const { documentType, file, existingDocument } = duplicateFileInfo;
        
        try {
            console.log('🗑️ FRONTEND: Deleting existing document:', existingDocument.Id);
            
            // Delete the existing document first
            await axiosInstance.delete(`/api/docuware/general-partner-info-documents/delete/${existingDocument.Id}/`);
            
            console.log('✅ FRONTEND: Existing document deleted successfully');
            
            // Close modal and proceed with upload using original filename
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
            
            await performFileUpload(documentType, file, file.name);
            
        } catch (error) {
            console.error('❌ FRONTEND: Error deleting existing document:', error);
            showError(getErrorMessage(error, 'Profile generation failed. Please try again.'));
            
            // Close modal even if deletion failed
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
        }
    };

    const handleDuplicateCancel = () => {
        console.log('❌ FRONTEND: User cancelled upload due to duplicate');
        
        // Simply close the modal and reset state
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
    };

    // View HAMSTIR document in a new browser tab
    const viewDocument = async (documentId, filename) => {
        try {
            const response = await axiosInstance.get(
                `/api/docuware/general-partner-info-documents/download/${documentId}/`,
                { responseType: 'blob' }
            );

            const contentType = response.headers['content-type'] || response.headers['Content-Type'] || 'application/octet-stream';
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');

            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (error) {
            console.error('Error viewing HAMSTIR document:', error);
            showError(getErrorMessage(error, 'Failed to open document. Please try downloading instead.'));
        }
    };

    // Download HAMSTIR document
    const downloadDocument = async (documentId, filename) => {
        try {
            const response = await axiosInstance.get(
                `/api/docuware/general-partner-info-documents/download/${documentId}/`,
                { responseType: 'blob' }
            );
            
            // Extract filename from headers if available
            let downloadFilename = filename;
            const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    downloadFilename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            if (!downloadFilename || downloadFilename === `General_Partner_Info_Document_${documentId}`) {
                const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
                let extension = '';
                if (contentType.includes('pdf')) {
                    extension = '.pdf';
                } else if (contentType.includes('image')) {
                    extension = '.jpg';
                } else if (contentType.includes('document') || contentType.includes('word')) {
                    extension = '.doc';
                }
                downloadFilename = `General_Partner_Info_Document_${documentId}${extension}`;
            }
            
            // Create blob URL and trigger download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log(`Downloaded HAMSTIR document ${documentId} as "${downloadFilename}"`);
            
        } catch (error) {
            console.error('Error downloading HAMSTIR document:', error);
            showError(getErrorMessage(error, 'Profile generation failed. Please try again.'));
        }
    };

    // Delete HAMSTIR document
    const deleteDocument = async (documentId, filename) => {
        try {
            await axiosInstance.delete(`/api/docuware/general-partner-info-documents/delete/${documentId}/`);
            
            // Refresh document list
            fetchDocuments();
            
            showSuccess(`Document "${filename}" deleted successfully`);
            
        } catch (error) {
            console.error('Error deleting HAMSTIR document:', error);
            showError(getErrorMessage(error, 'Profile generation failed. Please try again.'));
        }
    };

    // Build Partner Profile Summary function (simplified)
    const buildPartnerProfileSummary = async () => {
        if (!partnerIdentifier.trim() && selectedFiles.length === 0) {
            showError('Please enter partner information or select files for analysis');
            return;
        }

        console.log('🏗️ FRONTEND: Starting HAMSTIR Partner Profile Summary Building');
        console.log(`🏗️ Partner identifier: ${partnerIdentifier}`);
        console.log(`🏗️ Selected files count: ${selectedFiles.length}`);

        setSubmittingBuild(true);
        clearBuildRun();

        try {
            const mappedFiles = selectedFiles.map(file => ({
                document_id: file.document_id,
                filename: file.filename,
                subject: file.subject
            }));
            
            const requestPayload = {
                partner_identifier: partnerIdentifier,
                selected_documents: mappedFiles,
                partner_id: profileData?.id || null
            };

            // Answers straight away with a queued run; the summary arrives through
            // handleBuildFinished once the background service has finished.
            const response = await axiosInstance.post('/api/crm/build-partner-profile/', requestPayload);
            startBuildRun(response.data);
        } catch (error) {
            console.error('❌ FRONTEND: Error starting partner profile summary:', error);
            showError(getErrorMessage(error, 'Profile summary could not be started. Please try again.'));
        } finally {
            setSubmittingBuild(false);
        }
    };

    // Apply partner summary to profile (handles both new and existing profiles)
    const applyPartnerSummary = async (summary) => {
        try {
            console.log('🔄 FRONTEND: Applying partner overview summary...');
            console.log('📊 Summary to apply:', summary.substring(0, 200) + '...');

            // Check if this is a new profile (not saved yet) or existing profile
            if (!profileData?.id || isNewProfile) {
                console.log('📝 FRONTEND: New profile detected - applying summary to form data only');
                
                // For new profiles, just update the frontend form data without backend call
                const updatedProfileData = {
                    partner_overview_summary: summary,
                    partner_overview_source_urls: buildSourceUrls,
                    hamstir_update_timestamp: new Date().toISOString()
                };
                
                if (onProfileUpdate) {
                    onProfileUpdate(updatedProfileData);
                    showSuccess('Partner overview summary applied to form! Please save the profile to persist this data.');
                } else {
                    // Fallback: update the summary in the current component's form
                    console.log('⚠️ No onProfileUpdate callback available, using fallback method');
                    showSuccess('Partner overview summary applied to form! Please save the profile to persist this data.');
                }
                
                setShowResultsModal(false);
                return;
            }

            // For existing profiles, save to backend as before
            console.log('💾 FRONTEND: Existing profile detected - saving summary to backend');
            const response = await axiosInstance.post('/api/crm/apply-profile-data/', {
                partner_id: profileData.id,
                partner_overview_summary: summary,
                partner_overview_source_urls: buildSourceUrls
            });

            if (response.data.success) {
                console.log('✅ FRONTEND: Partner summary applied successfully');
                console.log('📊 Updated profile data:', response.data.updated_profile);
                
                // Update the form data with the response
                if (response.data.updated_profile && onProfileUpdate) {
                    // Add a timestamp to force re-renders across all tabs
                    const updatedProfileWithTimestamp = {
                        ...response.data.updated_profile,
                        hamstir_update_timestamp: new Date().toISOString()
                    };
                    onProfileUpdate(updatedProfileWithTimestamp);
                    showSuccess('Partner overview summary applied successfully and updated in form!');
                } else {
                    showSuccess('Partner overview summary applied successfully! Please refresh the page to see the updated data.');
                }
                
                setShowResultsModal(false);
            } else {
                console.error('❌ FRONTEND: Apply failed:', response.data);
                showError('Failed to apply partner overview summary');
            }
        } catch (error) {
            console.error('❌ FRONTEND: Error applying partner summary:', error);
            showError(`Failed to apply partner summary: ${error.response?.data?.error || error.message}`);
        }
    };

    // Function to get employee info by Django user ID (same as AI Notes)
    const getEmployeeInfoByUserId = async (djangoUserId) => {
        if (!djangoUserId) return null;
        
        // Check cache first
        if (employeeInfoCache[djangoUserId]) {
            return employeeInfoCache[djangoUserId];
        }
        
        try {
            // For current user, use their own info
            if (currentUserInfo && currentUserInfo.user_id === parseInt(djangoUserId)) {
                const employeeInfo = {
                    first_name: currentUserInfo.first_name,
                    last_name: currentUserInfo.last_name,
                    position: currentUserInfo.position,
                    department: currentUserInfo.department,
                    office: currentUserInfo.office,
                    employee_id: currentUserInfo.employee_id
                };
                
                // Cache the result
                setEmployeeInfoCache(prev => ({
                    ...prev,
                    [djangoUserId]: employeeInfo
                }));
                
                return employeeInfo;
            } else {
                // For other users, return placeholder
                const placeholderInfo = {
                    first_name: 'User',
                    last_name: `ID ${djangoUserId}`,
                    position: 'Unknown',
                    department: 'Unknown',
                    office: 'Unknown',
                    employee_id: djangoUserId
                };
                
                return placeholderInfo;
            }
        } catch (error) {
            console.error(`Error fetching employee info for user ID ${djangoUserId}:`, error);
            return null;
        }
    };

    // Extract document metadata for display (same pattern as AI Notes)
    const getDocumentInfo = (document) => {
        const fields = document.Fields || [];
        const getFieldValue = (fieldName) => {
            const field = fields.find(f => f.FieldName === fieldName);
            return field?.Item || '';
        };
        
        const subject = getFieldValue('SUBJECT');
        
        // For HAMSTIR documents, SUBJECT field now contains the actual filename
        // This follows the same pattern as banking documents
        const originalFilename = subject && subject !== 'General Partner Info Document' ? subject : null;
        const fallbackFilename = `general_partner_info_document_${document.Id}.pdf`;
        const filename = originalFilename || fallbackFilename;
        
        const dateUploaded = getFieldValue('DATE_UPLOADED');
        const employeeId = getFieldValue('EMPLOYEE_ID');
        
        const nameRow = hamstirDisplayNames?.[String(document.Id)] || null;
        const aiDisplayName = nameRow?.display_name || null;
        
        const docInfo = {
            filename,
            displayName: aiDisplayName || filename,
            aiDisplayName,
            nameRow,
            onNameRow: (updated) => setHamstirDisplayNames((prev) => ({ ...(prev || {}), [String(document.Id)]: updated })),
            dateUploaded,
            subject,
            fileSize: document.FileSize || 0,
            documentId: document.Id,
            employeeId
        };
        
        return docInfo;
    };

    // Format date for display (same as AI Notes)
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        
        try {
            let date;
            
            if (typeof dateString === 'string') {
                // Check for .NET JSON date format
                const netDateMatch = dateString.match(/\/Date\((\d+)\)\//);
                if (netDateMatch) {
                    const timestamp = parseInt(netDateMatch[1], 10);
                    date = new Date(timestamp);
                } else {
                    // Handle regular ISO format dates
                    const cleanDateString = dateString.replace(/Z+$/, 'Z').trim();
                    date = new Date(cleanDateString);
                }
            } else {
                date = new Date(dateString);
            }
            
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (error) {
            console.error('Date formatting error:', error);
            return 'Invalid Date';
        }
    };

    // Format file size (same as AI Notes)
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="crm-form-section" style={{ marginBottom: '2rem' }}>
            <div style={{
                background: 'linear-gradient(135deg, #f3f7ee 0%, #e8efdd 100%)',
                border: '2px solid #4a6b25',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem'
            }}>
                <h2 className="crm-section-title" style={{ 
                    color: '#4a6b25', 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                }}>
                    HAMSTIR Partner Profile Builder
                </h2>
                
                {/* Info text */}
                <div style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '0.5rem',
                    marginBottom: '1.5rem',
                    fontSize: '0.875rem',
                    color: '#1e40af',
                    lineHeight: '1.5'
                }}>
                    <strong>How to use HAMSTIR Builder:</strong> Enter partner information (name, website, or other identifying details) and/or select documents from below. The system will search the web, analyze documents, and generate a comprehensive partner overview summary that you can review and apply to your profile.
                </div>
                
                {/* Partner Identifier Input */}
                <div className="crm-input-group" style={{ marginBottom: '1rem' }}>
                    <label className="crm-input-label">Partner Identifier</label>
                    <input
                        type="text"
                        className="crm-input"
                        value={partnerIdentifier}
                        onChange={(e) => setPartnerIdentifier(e.target.value)}
                        placeholder="Enter partner name, website, or other identifying information..."
                        disabled={building}
                    />
                </div>

                {/* Document Management Section */}
                <div style={{ marginBottom: '1rem' }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginBottom: '30px' 
                    }}>
                        <div>
                            <label className="crm-input-label">Partner Information Documents</label>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                Documents are automatically loaded and can be selected for analysis
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                type="button"
                                onClick={() => setShowDocuments(!showDocuments)}
                                style={{
                                    background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                                }}
                            >
                                {showDocuments ? 'Hide Documents' : 'View Documents'}
                            </button>
                            {showDocuments && (
                                <button
                                    type="button"
                                    onClick={fetchDocuments}
                                    disabled={loadingDocuments}
                                    style={{
                                        background: loadingDocuments ? '#6c757d' : 'linear-gradient(135deg, #5a7b35, #4a6b25)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0.5rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: loadingDocuments ? 'not-allowed' : 'pointer',
                                        opacity: loadingDocuments ? 0.6 : 1,
                                        transition: 'all 0.2s',
                                        boxShadow: loadingDocuments ? 'none' : '0 2px 4px rgba(74, 107, 37, 0.15)'
                                    }}
                                >
                                    {loadingDocuments ? 'Refreshing...' : '↻ Refresh'}
                                </button>
                            )}
                            {/* Upload button - always visible but disabled when profile not saved */}
                            <div style={{ position: 'relative' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowUploadModal(true)}
                                    disabled={uploading || isNewProfile || !profileData?.id}
                                    style={{
                                        background: (uploading || isNewProfile || !profileData?.id) ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0.5rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: (uploading || isNewProfile || !profileData?.id) ? 'not-allowed' : 'pointer',
                                        opacity: (uploading || isNewProfile || !profileData?.id) ? 0.6 : 1,
                                        transition: 'all 0.2s',
                                        boxShadow: (uploading || isNewProfile || !profileData?.id) ? 'none' : '0 2px 4px rgba(245, 158, 11, 0.15)'
                                    }}
                                >
                                    {uploading ? 'Uploading...' : 'Upload Document'}
                                </button>
                                {(isNewProfile || !profileData?.id) && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: '0',
                                        right: '0',
                                        marginTop: '0.25rem',
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        fontStyle: 'italic',
                                        wordWrap: 'break-word',
                                        lineHeight: '1.2'
                                    }}>
                                        Save profile first to upload documents
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Document Display */}
                    {showDocuments && (
                        <div className="hamstir-documents-container" style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            backgroundColor: '#f9fafb',
                            marginBottom: '1rem'
                        }}>
                            {loadingDocuments && (
                                <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center',
                                    color: '#6b7280'
                                }}>
                                    Loading HAMSTIR documents...
                                </div>
                            )}

                            {documentError && (
                                <div style={{ 
                                    padding: '1rem',
                                    backgroundColor: '#fef2f2',
                                    border: '1px solid #ef4444',
                                    borderRadius: '0.375rem',
                                    color: '#dc2626',
                                    margin: '1rem'
                                }}>
                                    Error: {documentError}
                                </div>
                            )}

                            {!loadingDocuments && !documentError && hamstirDocuments.length === 0 && (
                                <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center',
                                    color: '#6b7280'
                                }}>
                                    No partner information documents found for this partner
                                </div>
                            )}

                            <div className="hamstir-documents-list" style={{ padding: '0.5rem' }}>
                                {!loadingDocuments && !documentError && hamstirDocuments.map(document => (
                                    <HAMSTIRDocumentItem
                                        key={document.Id}
                                        document={document}
                                        onView={viewDocument}
                                        onDownload={downloadDocument}
                                        onDelete={(documentId, filename) => {
                                            setDocumentToDelete({ id: documentId, filename });
                                            setShowDeleteConfirmation(true);
                                        }}
                                        onSelect={(file, isSelected) => {
                                            console.log('📎 File selection changed:', { file, isSelected });
                                            if (isSelected) {
                                                setSelectedFiles(prev => {
                                                    const newSelection = [...prev, file];
                                                    console.log('✅ Adding file, new selection:', newSelection);
                                                    return newSelection;
                                                });
                                            } else {
                                                setSelectedFiles(prev => {
                                                    const newSelection = prev.filter(f => f.document_id !== file.document_id);
                                                    console.log('❌ Removing file, new selection:', newSelection);
                                                    return newSelection;
                                                });
                                            }
                                        }}
                                        selectedFiles={selectedFiles}
                                        getDocumentInfo={getDocumentInfo}
                                        formatDate={formatDate}
                                        formatFileSize={formatFileSize}
                                        getEmployeeInfoByUserId={getEmployeeInfoByUserId}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Selected files summary */}
                {selectedFiles.length > 0 && (
                    <div className="crm-input-group" style={{ marginBottom: '1rem' }}>
                        <label className="crm-input-label">Selected Files ({selectedFiles.length})</label>
                        <div className="selected-files-summary" style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem',
                            padding: '0.75rem',
                            backgroundColor: '#f9fafb'
                        }}>
                            {selectedFiles.map(file => (
                                <div key={file.document_id} className="selected-file-item" style={{
                                    fontSize: '0.875rem',
                                    color: '#374151',
                                    marginBottom: '0.25rem'
                                }}>
                                    {file.filename} ({file.subject})
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Build button with progress */}
                <div className="crm-input-group">
                    <button
                        type="button"
                        onClick={buildPartnerProfileSummary}
                        disabled={building || (!partnerIdentifier.trim() && selectedFiles.length === 0)}
                        style={{
                            background: building || (!partnerIdentifier.trim() && selectedFiles.length === 0) 
                                ? '#6c757d' 
                                : 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            marginTop: '20px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: building || (!partnerIdentifier.trim() && selectedFiles.length === 0) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: building || (!partnerIdentifier.trim() && selectedFiles.length === 0) 
                                ? 'none' 
                                : '0 4px 8px rgba(74, 107, 37, 0.25)',
                            minWidth: '200px'
                        }}
                                            >
                            {building ? 'Building Summary...' : 'Build Partner Profile Summary'}
                    </button>
                    
                    {building && (
                        <div className="build-loading" style={{ 
                            marginTop: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                        }}>
                            <div className="loading-spinner" style={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid #e5e7eb',
                                borderTop: '2px solid #4a6b25',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            <div className="loading-text" style={{
                                fontSize: '0.875rem',
                                color: '#6b7280'
                            }}>
                                Reading documents, searching the web and building the profile. Scanned documents can take several minutes; this updates by itself.
                            </div>
                            {buildRun && (
                                <div style={{ width: '100%', maxWidth: '28rem', marginTop: '0.75rem' }}>
                                    <ReadingProgress
                                        compact
                                        progress={buildRun.progress}
                                        startedAt={buildRun.started_at}
                                        leaveText="You can leave this tab — it carries on and shows the profile when you come back."
                                    />
                                </div>
                            )}
                            <style jsx>{`
                                @keyframes spin {
                                    0% { transform: rotate(0deg); }
                                    100% { transform: rotate(360deg); }
                                }
                            `}</style>
                        </div>
                    )}

                    {!building && <DocumentReadingRunPanel run={buildRun} onDismiss={clearBuildRun} />}
                </div>
            </div>

            {/* Upload Modal */}
            <HAMSTIRUploadModal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                documentTypes={HAMSTIR_DOCUMENT_TYPES}
                onDocumentSelect={handleFileUpload}
                uploading={uploading}
                profileId={profileData?.id}
                partnerName={profileData?.legal_entity_name}
            />

            {/* Results Modal */}
            <HAMSTIRResultsModal
                isOpen={showResultsModal}
                onClose={() => setShowResultsModal(false)}
                results={buildResults}
                onApplyData={applyPartnerSummary}
                isNewProfile={isNewProfile}
                profileData={profileData}
            />

            {/* Duplicate Detection Modal */}
            {showDuplicateModal && duplicateFileInfo && (
                <div className="confirmation-popup-overlay">
                    <div className="confirmation-popup">
                        <div className="confirmation-popup-message">
                            <h3 style={{ 
                                margin: '0 0 1rem 0', 
                                color: '#374151',
                                fontSize: '1.25rem',
                                fontWeight: '600'
                            }}>
                                Duplicate File Detected
                            </h3>
                            <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>
                                A file with the name "<strong>{duplicateFileInfo.file.name}</strong>" already exists in this partner's documents.
                            </p>
                            <p style={{ margin: '0', color: '#374151', fontSize: '0.95rem' }}>
                                What would you like to do?
                            </p>
                        </div>
                        <div className="confirmation-popup-buttons" style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            gap: '0.75rem',
                            marginTop: '1.5rem',
                            flexWrap: 'wrap' 
                        }}>
                            <button 
                                onClick={handleDuplicateKeepBoth}
                                disabled={uploading}
                                style={{
                                    background: uploading ? '#6c757d' : 'linear-gradient(135deg, #10b981, #059669)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '0.6rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s',
                                    minWidth: '90px'
                                }}
                            >
                                Keep Both
                            </button>
                            <button 
                                onClick={handleDuplicateReplace}
                                disabled={uploading}
                                style={{
                                    background: uploading ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '0.6rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s',
                                    minWidth: '90px'
                                }}
                            >
                                Replace
                            </button>
                            <button 
                                onClick={handleDuplicateCancel}
                                disabled={uploading}
                                style={{
                                    background: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '0.6rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s',
                                    minWidth: '90px'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirmation && documentToDelete && (
                <ConfirmationPopup
                    message={`Are you sure you want to delete "${documentToDelete.filename}"? This action cannot be undone.`}
                    onConfirm={() => {
                        deleteDocument(documentToDelete.id, documentToDelete.filename);
                        setShowDeleteConfirmation(false);
                        setDocumentToDelete(null);
                    }}
                    onCancel={() => {
                        setShowDeleteConfirmation(false);
                        setDocumentToDelete(null);
                    }}
                />
            )}
        </div>
    );
};

// HAMSTIRDocumentGroup component removed - no grouping needed

// Individual HAMSTIR document item following AI Notes pattern with selection checkbox
const HAMSTIRDocumentItem = ({ 
    document, 
    onView,
    onDownload, 
    onDelete, 
    onSelect,
    selectedFiles,
    getDocumentInfo, 
    formatDate, 
    formatFileSize, 
    getEmployeeInfoByUserId
}) => {
    const [employeeInfo, setEmployeeInfo] = useState(null);
    const [loadingEmployee, setLoadingEmployee] = useState(false);
    
    const docInfo = getDocumentInfo(document);
    
    // Create file object for selection
    const fileForSelection = {
        document_id: docInfo.documentId,
        filename: docInfo.filename,
        subject: docInfo.subject
    };
    
    const isSelected = selectedFiles.some(f => f.document_id === docInfo.documentId);
    
    // Fetch employee info when component mounts
    useEffect(() => {
        const fetchEmployeeInfo = async () => {
            if (!docInfo.employeeId) return;
            
            setLoadingEmployee(true);
            try {
                const empInfo = await getEmployeeInfoByUserId(docInfo.employeeId);
                setEmployeeInfo(empInfo);
            } catch (error) {
                console.error('Error fetching employee info for document:', error);
            } finally {
                setLoadingEmployee(false);
            }
        };
        
        fetchEmployeeInfo();
    }, [docInfo.employeeId, getEmployeeInfoByUserId]);
    
    const handleSelectionChange = (e) => {
        onSelect(fileForSelection, e.target.checked);
    };
    
    return (
        <div 
            className={`crm-hamstir-document-item ${isSelected ? 'selected' : ''}`}
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem',
                marginBottom: '0.5rem',
                backgroundColor: '#ffffff',
                borderRadius: '6px',
                border: isSelected ? '2px solid #4a6b25' : '1px solid #e5e7eb'
            }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flex: 1 }}>
                {/* Selection checkbox */}
                <input
                    type="checkbox"
                    className="crm-hamstir-file-checkbox"
                    checked={isSelected}
                    onChange={handleSelectionChange}
                />
                
                <div style={{ flex: 1 }}>
                    <div style={{ 
                        fontWeight: 'bold', 
                        color: '#495057',
                        marginBottom: '0.25rem',
                        fontSize: '0.95rem'
                    }} title={docInfo.displayName || docInfo.filename}>
                        {docInfo.displayName || docInfo.filename}
                    </div>
                    <SuggestedName row={docInfo.nameRow} onChanged={docInfo.onNameRow} />
                    {docInfo.aiDisplayName && docInfo.aiDisplayName !== docInfo.filename && (
                        <div style={{
                            fontSize: '0.75rem',
                            color: '#888',
                            marginBottom: '0.25rem',
                            wordBreak: 'break-all'
                        }} title={docInfo.filename}>
                            {docInfo.filename}
                        </div>
                    )}

                    <div style={{
                        fontSize: '0.85rem', 
                        color: '#6c757d',
                        marginBottom: '0.25rem'
                    }}>
                        Uploaded: {formatDate(docInfo.dateUploaded)} | Size: {formatFileSize(docInfo.fileSize)}
                    </div>
                    
                    {/* Employee Information Section */}
                    <div style={{ 
                        fontSize: '0.85rem', 
                        color: '#495057',
                        marginTop: '0.5rem',
                        padding: '0.4rem 0.6rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6'
                    }}>
                        <strong>Uploaded by:</strong>
                        {loadingEmployee ? (
                            <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>Loading...</span>
                        ) : employeeInfo ? (
                            <div style={{ marginTop: '0.25rem' }}>
                                <div><strong>{employeeInfo.first_name} {employeeInfo.last_name}</strong></div>
                                {employeeInfo.position && (
                                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                        {employeeInfo.position}
                                        {employeeInfo.department && ` • ${employeeInfo.department}`}
                                        {employeeInfo.office && ` • ${employeeInfo.office}`}
                                    </div>
                                )}
                                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                    Employee ID: {employeeInfo.employee_id}
                                </div>
                            </div>
                        ) : (
                            <span style={{ marginLeft: '0.5rem', fontStyle: 'italic', color: '#6c757d' }}>
                                Employee info not available
                            </span>
                        )}
                    </div>
                    
                    <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#6c757d',
                        marginTop: '0.25rem'
                    }}>
                        Document ID: {docInfo.documentId}
                    </div>
                </div>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                <button
                    onClick={() => onView(docInfo.documentId, docInfo.filename)}
                    title="Open document in new tab"
                    style={{
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minWidth: '80px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.15)'
                    }}
                >
                    View
                </button>
                <button
                    onClick={() => onDownload(docInfo.documentId, docInfo.filename)}
                    style={{
                        background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minWidth: '80px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                    }}
                >
                    Download
                </button>
                <button
                    onClick={() => onDelete(docInfo.documentId, docInfo.filename)}
                    style={{
                        background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minWidth: '80px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(220, 38, 38, 0.15)'
                    }}
                >
                    Delete
                </button>
            </div>
        </div>
    );
};

export default HAMSTIRBuilderBlock; 